import axios from 'axios';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { generateHeaders } from 'utils/APIUtils';
import { Reject } from '../Reject';
import { handleApiError } from '../handleApiError';
import { logout } from './auth';
import { Spot } from 'domain/Spot';

interface StateWithAuth {
    auth: { jwt: string };
}

export interface PendingSpot {
    id?: string;
    name: string;
    description: string;
    lat?: number;
    lng?: number;
    privacy?: string;
    anonymity?: string;
    privacyApproved?: string;
    dateAdded?: string;
    pictureIds?: string[];
    categoryIds?: string[];
    hazardsIds?: string[];
    youTubeId?: string;
    idUser?: string;
}

interface PendingSpotsState {
    spots: PendingSpot[];
    loading: boolean;
    approvingId: string | null;
    error: string | null;
    selectedSpot: Spot | null;
    loadingDetail: boolean;
}

const initialState: PendingSpotsState = {
    spots: [],
    loading: false,
    approvingId: null,
    error: null,
    selectedSpot: null,
    loadingDetail: false,
};

export const fetchPendingSpots = createAsyncThunk<PendingSpot[], void, Reject>(
    'pendingSpots/fetch',
    async (_, thunkAPI) => {
        try {
            const { auth } = thunkAPI.getState() as StateWithAuth;
            const headers = generateHeaders(auth.jwt);
            const res = await axios.get<PendingSpot[]>(`${process.env.REACT_APP_API_URL}/admin/spots/pending`, { headers });
            return res.data;
        } catch (err) {
            return thunkAPI.rejectWithValue(handleApiError(err));
        }
    }
);

export const fetchSpotDetail = createAsyncThunk<Spot, string, Reject>(
    'pendingSpots/fetchDetail',
    async (id, thunkAPI) => {
        try {
            const { auth } = thunkAPI.getState() as StateWithAuth;
            const headers = generateHeaders(auth.jwt);
            const res = await axios.get<Spot>(`${process.env.REACT_APP_API_URL}/admin/spot/${id}`, { headers });
            return res.data;
        } catch (err) {
            return thunkAPI.rejectWithValue(handleApiError(err));
        }
    }
);

export const approvePendingSpot = createAsyncThunk<string, string, Reject>(
    'pendingSpots/approve',
    async (id, thunkAPI) => {
        try {
            const { auth } = thunkAPI.getState() as StateWithAuth;
            const headers = generateHeaders(auth.jwt);
            await axios.post(`${process.env.REACT_APP_API_URL}/admin/spot/${id}`, {}, { headers });
            return id;
        } catch (err) {
            return thunkAPI.rejectWithValue(handleApiError(err));
        }
    }
);

const pendingSpotsSlice = createSlice({
    name: 'pendingSpots',
    initialState,
    reducers: {
        clearSelectedSpot(state) {
            state.selectedSpot = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPendingSpots.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchPendingSpots.fulfilled, (state, action) => {
                state.loading = false;
                state.spots = action.payload;
            })
            .addCase(fetchPendingSpots.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message ?? 'Failed to load pending spots';
            })
            .addCase(fetchSpotDetail.pending, (state) => {
                state.loadingDetail = true;
                state.selectedSpot = null;
            })
            .addCase(fetchSpotDetail.fulfilled, (state, action) => {
                state.loadingDetail = false;
                state.selectedSpot = action.payload;
            })
            .addCase(fetchSpotDetail.rejected, (state) => {
                state.loadingDetail = false;
            })
            .addCase(approvePendingSpot.pending, (state, action) => {
                state.approvingId = action.meta.arg;
            })
            .addCase(approvePendingSpot.fulfilled, (state, action) => {
                state.approvingId = null;
                state.spots = state.spots.filter((s) => s.id !== action.payload);
            })
            .addCase(approvePendingSpot.rejected, (state) => {
                state.approvingId = null;
            })
            .addCase(logout, () => initialState);
    },
});

export const { clearSelectedSpot } = pendingSpotsSlice.actions;
export default pendingSpotsSlice.reducer;
